import React, { useContext, useEffect, useState, useRef } from 'react';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import { useHashAsState } from 'tbk-components/src/hooks';
import ElasticMapper from 'tbk-components/src/services/ElasticMapper';
import qs from 'qs';
import Loading from 'tbk-components/src/components/Loading';
import cn from 'classnames';
import Image, { IImage } from 'tbk-components/src/components/Image';
import DefaultFilterBar from './CustomFilterBar';
import AltFilterBar from './CustomFilterBarAlt';
import CustomBlogRollTile from './CustomBlogRollTile';
import Pagination from './Pagination';

import logoSymbol from '../../assets/logo-symbol.svg';

export interface IBlogAuthor {
	name?: string;
	url?: string;
	image?: IImage;
}

export interface IBlogPostCategory {
	name?: string;
	url?: string;
}

export interface IBlogPostTag {
	name?: string;
	url?: string;
}

export interface IBlogPost {
	image?: IImage;
	heading?: string;
	author?: IBlogAuthor;
	readTime?: string;
	excerpt?: string;
	content?: string;
	url?: string;
	categories?: IBlogPostCategory[];
	tags?: IBlogPostTag[];
	date?: string;
}

export interface ICustomBlogRollProps extends IBaseElementProps {
	/**
	 * @noUI
	 */
	context?: any;
	/**
	 * @noUI
	 */
	assets?: any;
	/**
	 * @noUI
	 */
	archivePage?: string;
	showHeading?: boolean;
	/**
	 * Turn on to move heading to the first of tile in the list of posts. Tablet/Desktop only.
	 */
	useAlternateHeadingPlacement?: boolean;
	heading?: string;
	showSeeMore?: boolean;
	seeMoreLabel?: string;
	/**
	 * Turn this on to show only a list of latest posts.
	 */
	showOnlyLatestPosts?: boolean;
	/**
	 * Set the number of posts to show. Entering a number will prevent pagination UI from appearing. Leave blank to allow pagination
	 */
	numberOfPostsToShow?: number;
	showPagination?: boolean;
	/**
	 * @noUI
	 */
	posts?: {
		total: number;
		hits: any[];
	};
	/**
	 * @noUI
	 */
	fetchArgs?: any;
	showFilterBar?:boolean;
	/**
	 * @noUI
	 * prevent WP user from accessing this option. Keeping it here just in case
	 */
	filterBarOnly?: boolean;
	useAlternateFilterBar?: boolean;
	/**
	 * @noUI
	 * prevent WP user from accessing this option. Keeping it here just in case
	 */
	filterBarTargetUrl?: string;
	filterSearchPlaceholder?: string;
	filterLabel?: string;
	displaySearchTaxonomies?: boolean;
	/**
	 * Text that shows if there are no results from filtering.
	 */
	noResultsMessage?: string;
	/**
	 * Turn on to show a message instead of search results before user interacts.
	 */
	enableStartScreen?: boolean;
	/**
	 * Text that shows at the start of the search process.
	 */
	startScreenMessage?: string;
}

/**
 * Show posts as a collection.
 *
 * @block
 * @icon media-document
 * @requiresHooks
 */
const CustomBlogRoll: React.FC<ICustomBlogRollProps> = withAppContext(
	({
		className,
		classNames = [],
		context,
		heading,
		archivePage,
		showHeading = false,
		useAlternateHeadingPlacement = false,
		showSeeMore = false,
		seeMoreLabel = 'Read on',
		showOnlyLatestPosts = false,
		numberOfPostsToShow,
		showPagination = true,
		posts,
		fetchArgs,
		showFilterBar = true,
		filterBarOnly = false,
		useAlternateFilterBar = false,
		filterLabel,
		displaySearchTaxonomies = true,
		filterSearchPlaceholder = 'Search',
		filterBarTargetUrl = '',
		noResultsMessage = 'No results found.  Please adjust your filters',
		enableStartScreen = true,
		startScreenMessage = 'Please enter your search above',
	}) => {
		// @ts-ignore
		const { assets: contextAssets, Api } = useContext(context);
		const [assets, setAssets] = useState<any>(null);
		const [blogRoll, setBlogRoll] = useState<any>(null);
		const [termMap, setTermMap] = useState<any>(null);
		const [total, setTotal] = useState<number | null>(null);
		const [loading, setLoading] = useState<boolean>(false);
		const [startScreen, setStartScreen] = useState<boolean>(false);
		const [filterBarState, setFilterBarState] = useState({});
		const taxonomy_parameters = ((taxonomies) => {
			const t: any = {};
			(taxonomies || []).forEach((tax: any) => {
				t[tax.taxonomy] = 'csv';
			});
			return t;
		})(assets?.taxonomies);
		const [state, hash, updateState, normalizeState] = useHashAsState({
			parameters: {
				page: 'int',
				...taxonomy_parameters,
				search: 'string',
			},
			defaults: {
				search: '',
				page: 1,
			},
		});

		// ref to pass down to pagination so it can force a scroll to top of blogRoll when user changes page.
		const topRef = useRef(null);

		// Which filter bar component to use depends on useAlternateFilterBar prop
		const FilterBar = !useAlternateFilterBar ? DefaultFilterBar : AltFilterBar;

		// Here's the effect that actually gets the posts from the server whenever the search has changed.
		useEffect(() => {
			if (posts) {
				setTotal(posts.total);
				setBlogRoll(ElasticMapper(posts));
				return;
			}
			if (!assets) {
				return;
			}
			let args = qs.parse(hash.replace(/^#!/, ''), {
				ignoreQueryPrefix: true,
			});
			args.posts_per_page = assets?.posts_per_page;
			if (fetchArgs) {
				args = {
					...fetchArgs,
					...args,
				};
			}

			// override whatever is in args to only show posts
			if (showOnlyLatestPosts) {
				args.post_type = 'post';
			}
			if (numberOfPostsToShow) {
				args.posts_per_page = `${numberOfPostsToShow}`; // cast to string
			}
			setBlogRoll(null);
			setLoading(true);

			// show the start screen if blogRoll is NOT set to show latest posts AND search state hasn't been changed
			if (enableStartScreen && !showOnlyLatestPosts && (Object.keys(state).length == 2 && state.search == '' && state.page == 1)) {
				setStartScreen(true);
				setLoading(false);
			} else {
				Api.getBlogRoll(args)
					.then((results: any) => {
						setTotal(results.total);
						setBlogRoll(ElasticMapper(results));
					})
					.finally(() => {
						setLoading(false);
						setStartScreen(false);
					});
			}
		}, [
			hash,
			assets,
			fetchArgs,
			posts,
			Api,
			showOnlyLatestPosts,
			numberOfPostsToShow,
		]);

		useEffect(() => {
			const assetReqArgs = { ...fetchArgs };
			const setAssetsAndTermMap = (results: any) => {
				setAssets(results);
				const map: any = {};
				if (results.taxonomies) {
					results.taxonomies.forEach((taxonomy: any) => {
						taxonomy.terms.forEach((term: any) => {
							map[term.term_id] = term;
						});
					});
				}
				setTermMap(map);
			};
			if (contextAssets) {
				setAssetsAndTermMap(contextAssets);
			} else {
				// override whatever is in fetchArgs to only show posts
				if (showOnlyLatestPosts) {
					assetReqArgs.post_type = 'post';
				}
				Api.getBlogRollAssets(assetReqArgs).then((results: any) => {
					setAssetsAndTermMap(results);
				});
			}
		}, [contextAssets, Api, fetchArgs]);

		if (!assets) {
			return (
				<div className="loading-wrapper">
					<Loading />
				</div>
			);
		} if (filterBarOnly) {
			const onChangeFilterBar = (change: any) => {
				setFilterBarState((s) => ({ ...s, ...change }));
			};
			return (
				<FilterBar
					filters={filterBarState}
					searchPlaceholder={filterSearchPlaceholder}
					searchTaxonomies={displaySearchTaxonomies}
					filterLabel={filterLabel}
					assets={assets}
					targetUrl={
						filterBarTargetUrl +
						normalizeState(filterBarState, 'query')
					}
					onChange={onChangeFilterBar}
				/>
			);
		}

		const includeHeader = showFilterBar && !posts; // if we already have posts, we're not going to include the header

		// @ts-ignore
		const elClassName = className || cn(...classNames);

		return (
			<div className={elClassName} ref={topRef}>
				{includeHeader && (
					<FilterBar
						SearchPlaceholder={filterSearchPlaceholder}
						searchTaxonomies={displaySearchTaxonomies}
						filterLabel={filterLabel}
						filters={state}
						assets={assets}
						onChange={updateState}
					/>
				)}
				<div className="blogroll-results__container">
					<div className="blogroll-results__container-inner">
						{loading ? (
							<div className="loading-wrapper">
								<Loading />
							</div>
						) : (
							<>
								{startScreen ? (
									<div className="starting-screen">
										{startScreenMessage}
									</div>
								) : (
									<>
										{showHeading || showSeeMore ? (
											<div className="blogroll-results__heading">
												{showHeading &&
											!useAlternateHeadingPlacement &&
											heading ? (
												<h2>{heading}</h2>
													) : null}
												{showSeeMore && archivePage ? (
													<a
														className="btn btn-primary btn-sm"
														href={archivePage}
													>
														{seeMoreLabel}
													</a>
												) : null}
											</div>
										) : (
											''
										)}
										{total ? (
											<>
												<div className={cn('blogroll-results__list', fetchArgs?.post_type && `blogroll-results__list--${fetchArgs.post_type}`)}>
													{showHeading && useAlternateHeadingPlacement && (
														<div className="blogroll-results__item leading-tile">
															<Image
																src={logoSymbol}
																height={70}
																width={64}
																className="d-none d-md-block"
															/>
															{heading && (
																<h2>{heading}</h2>
															)}
														</div>
													)}
													{(blogRoll || []).map(
														(blogroll: any) => (
															<CustomBlogRollTile
																key={blogroll.ID}
																blogroll={blogroll}
																termMap={termMap}
															/>
														),
													)}
												</div>
												{showPagination && !numberOfPostsToShow && (
													<Pagination
														className="pagination-wrapper"
														page={state.page || 1}
														perPage={
															fetchArgs?.posts_per_page ||
															assets?.posts_per_page
														}
														total={total}
														onChange={(page: any) =>
															updateState({ page })}
														showAllLabel={false}
														topRef={topRef}
													/>
												)}
											</>
										) : (
											<p className="no-results">
												{noResultsMessage}
											</p>
										)}
									</>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		);
	},
);

export default CustomBlogRoll;
